import {
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInQuarters,
  differenceInSeconds,
  differenceInWeeks,
  differenceInYears,
  toDate,
} from "date-fns";
import {
  secondsInDay,
  secondsInHour,
  secondsInMinute,
  secondsInMonth,
  secondsInQuarter,
  secondsInWeek,
  secondsInYear,
} from "date-fns/constants";

export interface IntlFormatDistanceOptions {
  localeMatcher?: Intl.RelativeTimeFormatLocaleMatcher;
  /** 'auto' (e.g. `yesterday`) or 'always' (e.g. `1 day ago`). Defaults to 'auto'. */
  numeric?: Intl.RelativeTimeFormatNumeric;
  style?: Intl.RelativeTimeFormatStyle;
}

/**
 * Formats the distance between two dates via `Intl.RelativeTimeFormat`, picking the largest
 * unit that fits (seconds → minutes → hours → days → weeks → months → quarters → years).
 *
 * Fork of date-fns' `intlFormatDistance` that measures **elapsed** time rather than calendar
 * boundaries: whole elapsed days (seconds / 86400, since date-fns' `differenceInDays` is wall-clock
 * based and reads 0 days for 24 real hours across a daylight-saving change) and `differenceInWeeks`/`Months`/...
 * instead of the `differenceInCalendar*` variants. So 23:00 → 01:00 next day reads "2 hours ago", not "yesterday". Don't swap this
 * for `intlFormatDistance` without re-baselining `formatRelative.test.ts` — the two disagree
 * near every unit boundary.
 */
export function formatRelative<DateType extends Date>(
  date: DateType | number,
  baseDate: DateType | number,
  options?: IntlFormatDistanceOptions,
): string {
  let value = 0;
  let unit: Intl.RelativeTimeFormatUnit;
  const dateLeft = toDate(date);
  const dateRight = toDate(baseDate);

  const diffInSeconds = differenceInSeconds(dateLeft, dateRight); // The smallest unit

  if (Math.abs(diffInSeconds) < secondsInMinute) {
    value = differenceInSeconds(dateLeft, dateRight);
    unit = "second";
  } else if (Math.abs(diffInSeconds) < secondsInHour) {
    value = differenceInMinutes(dateLeft, dateRight);
    unit = "minute";
  } else if (Math.abs(diffInSeconds) < secondsInDay) {
    value = differenceInHours(dateLeft, dateRight);
    unit = "hour";
  } else if (
    Math.abs(diffInSeconds) < secondsInWeek &&
    (value = Math.trunc(diffInSeconds / secondsInDay)) &&
    Math.abs(value) < 7
  ) {
    unit = "day";
  } else if (Math.abs(diffInSeconds) < secondsInMonth) {
    value = differenceInWeeks(dateLeft, dateRight);
    unit = "week";
  } else if (Math.abs(diffInSeconds) < secondsInQuarter) {
    value = differenceInMonths(dateLeft, dateRight);
    unit = "month";
  } else if (Math.abs(diffInSeconds) < secondsInYear && differenceInQuarters(dateLeft, dateRight) < 4) {
    // Filter out cases that are less than a year but match 4 quarters
    value = differenceInQuarters(dateLeft, dateRight);
    unit = "quarter";
  } else {
    value = differenceInYears(dateLeft, dateRight);
    unit = "year";
  }

  return new Intl.RelativeTimeFormat(undefined, {
    localeMatcher: options?.localeMatcher,
    numeric: options?.numeric ?? "auto",
    style: options?.style,
  }).format(value, unit);
}
